import {z} from 'zod';

/**
 * Zod schema for creator profile data included with social media posts
 */
export const SocialMediaPostProfileSchema = z.object({
  id: z.string(),
  displayName: z.string().nullable(),
  imageUrl: z.string().nullable(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  birthDate: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  rating: z.number().nullable().optional(),
  ratingsCount: z.number().nullable().optional(),
  completedPaidGigsCount: z.number().nullable().optional(),
  email: z.string().nullable().optional(),
});

/**
 * Base schema for social media post data
 *
 * Field nullability is based on the swap_matches DB schema:
 * - Fields with `default: X, null: false` are required (always present)
 * - Fields without null constraint are nullable
 * - Fields that may not exist in the response are `.optional()`
 */
const SocialMediaPostBaseSchema = z.object({
  id: z.string(),
  // Nullable fields (no DB constraint or explicitly nullable)
  body: z.string().nullable(),
  clickthroughCount: z.number().nullable().optional(), // No default, no null constraint
  closeupImpressionCount: z.number().nullable().optional(), // No default, no null constraint
  engagementRate: z.number().nullable(), // Float with no default
  isTopPerformerByEngagement: z.boolean().nullable(), // Computed, can be nil
  likeCount: z.number().nullable(), // Has default but no null: false
  link: z.string().nullable(),
  postedAt: z.string().nullable(), // DB: no null constraint
  repostCount: z.number().nullable().optional(), // No default, no null constraint
  thumbnailUrl: z.string().nullable(),
  viewCount: z.number().nullable(), // Model accessor returns nil if 0
  // Required fields (DB: default: X, null: false)
  // Snapshot-overridable fields are nullable — when date-filtered, posts
  // without snapshot data return null so the UI shows "—" instead of
  // misleading all-time values.
  commentCount: z.number().nullable(), // Snapshot-overridable
  engagementRateOrganic: z.number().nullable(), // Nullable when date-filtered
  followerCount: z.number(), // DB: default: 0, null: false
  impressionCount: z.number().nullable(), // Snapshot-overridable
  reachCount: z.number().nullable(), // Snapshot-overridable
  replyCount: z.number(), // DB: default: 0, null: false
  savedCount: z.number().nullable(), // Snapshot-overridable
  shareCount: z.number().nullable(), // Snapshot-overridable
  totalInteractionsCount: z.number().nullable(), // Snapshot-overridable; likes + comments + shares + saves, derived server-side
  videoAggregatedPlaysCount: z.number().nullable(), // Snapshot-overridable
  videoAverageWatchTime: z.number().nullable().optional(), // Nullable, only present for IG Reels
  videoViewsOrganic: z.number(), // DB: default: 0, null: false
  videoViewsPaid: z.number(), // DB: default: 0, null: false
  emv: z.number().nullable().optional(),
  favoritesOrganic: z.number().nullable().optional(), // TikTok-only; nullable when unavailable
  favoritesPaid: z.number().nullable().optional(), // TikTok-only; nullable when unavailable
  // Optional relationship
  profile: SocialMediaPostProfileSchema.optional(),
});

/**
 * Zod schema for raw social media post data from the API (before transformation)
 *
 * This represents the actual API response structure where:
 * - Some fields may be missing (optional)
 * - Some fields may be null
 * - Required fields are guaranteed by the DB schema
 *
 * Derived from base schema with additional optionality for fields that may be missing.
 */
export const RawSocialMediaPostSchema = SocialMediaPostBaseSchema.extend({
  // Fields that may be missing in raw API response
  body: z.string().nullable().optional(),
  createdAt: z.string().optional(), // May be missing in raw response
  engagementRate: z.number().nullable().optional(),
  isTopPerformerByEngagement: z.boolean().nullable().optional(),
  likeCount: z.number().nullable().optional(),
  link: z.string().nullable().optional(),
  postedAt: z.string().nullable().optional(),
  socialStream: z.enum(['feed', 'story', 'reels']).optional(),
  thumbnailUrl: z.string().nullable().optional(),
  url: z.string().optional(), // May be missing in raw response
  viewCount: z.number().nullable().optional(),
});

/**
 * Zod schema for social media post data after transformation and validation
 *
 * This represents the normalized, validated structure used throughout the app.
 * All fields are properly typed and required fields are guaranteed.
 *
 * Derived from base schema with required fields for normalized data.
 */
export const SocialMediaPostSchema = SocialMediaPostBaseSchema.extend({
  // Fields that are required after normalization (defaults applied in transformPost)
  createdAt: z.string(), // Required after normalization (defaults to '')
  socialStream: z.enum(['feed', 'story', 'reels']).optional(),
  url: z.string(), // Required after normalization (defaults to '')
});

/**
 * Zod schema for campaign metrics totals by network
 * Note: Keys are in camelCase as the API response is transformed by mapKeysToCase
 */
export const CampaignMetricsTotalsByNetworkSchema = z.object({
  commentCount: z.number().optional(),
  creatorCount: z.number().optional(),
  engagementRate: z.number().optional(),
  followerCount: z.number().optional(),
  impressionCount: z.number().optional(),
  likeCount: z.number().optional(),
  reachCount: z.number().optional(),
  replyCount: z.number().optional(),
  savedCount: z.number().optional(),
  shareCount: z.number().optional(),
  totalInteractionsCount: z.number().optional(),
  videoAggregatedPlaysCount: z.number().optional(),
  viewCount: z.number().optional(),
  videoViewsOrganic: z.number().optional(),
  videoViewsPaid: z.number().optional(),
  emv: z.number().nullable().optional(),
});

/**
 * Zod schema for campaign metrics averages by network
 * Note: Keys are in camelCase as the API response is transformed by mapKeysToCase
 */
export const CampaignMetricsAveragesByNetworkSchema = z.object({
  commentCountPerPost: z.number().optional(),
  engagementRatePerPost: z.number().optional(),
  followerCountPerPost: z.number().optional(),
  followersReachedPerPost: z.number().optional(),
  impressionCountPerPost: z.number().optional(),
  likeCountPerPost: z.number().optional(),
  reachCountPerPost: z.number().optional(),
  replyCountPerPost: z.number().optional(),
  savedCountPerPost: z.number().optional(),
  shareCountPerPost: z.number().optional(),
  totalInteractionsCountPerPost: z.number().optional(),
  videoAggregatedPlaysCountPerPost: z.number().optional(),
  viewCountPerPost: z.number().optional(),
});

export const EmvFormulaSchema = z.object({
  cpmUsd: z.number(),
  cpeUsd: z.number(),
  tooltip: z.string(),
});

/**
 * Zod schema for campaign stats metadata
 */
export const CampaignStatsMetadataSchema = z.object({
  totalsByNetwork: CampaignMetricsTotalsByNetworkSchema.optional(),
  averagesByNetwork: CampaignMetricsAveragesByNetworkSchema.optional(),
  postCount: z.number().optional(),
  totalVideoViewsOrganic: z.number().optional(),
  totalVideoViewsPaid: z.number().optional(),
  organicStatsRefreshedAt: z.string().nullable().optional(),
  dateFiltered: z.boolean().optional(),
  emvFormula: EmvFormulaSchema.optional().catch(undefined),
});

/**
 * Zod schema for pagination metadata
 */
export const PaginationMetaSchema = z.object({
  currentPage: z.number(),
  nextPage: z.number().nullable(),
  prevPage: z.number().nullable(),
  totalEntries: z.number(),
  totalPages: z.number(),
});

/**
 * Zod schema for the complete API response
 */
export const SocialMediaPostsResponseSchema = z.object({
  data: z.array(SocialMediaPostSchema),
  meta: z.object({
    campaignStats: CampaignStatsMetadataSchema.optional(),
    pagination: PaginationMetaSchema,
  }),
});

// TypeScript types inferred from Zod schemas
export type SocialMediaPostProfile = z.infer<
  typeof SocialMediaPostProfileSchema
>;
export type RawSocialMediaPost = z.infer<typeof RawSocialMediaPostSchema>;
export type SocialMediaPost = z.infer<typeof SocialMediaPostSchema>;
export type CampaignMetricsTotalsByNetwork = z.infer<
  typeof CampaignMetricsTotalsByNetworkSchema
>;
export type CampaignMetricsAveragesByNetwork = z.infer<
  typeof CampaignMetricsAveragesByNetworkSchema
>;
export type EmvFormula = z.infer<typeof EmvFormulaSchema>;
export type CampaignStatsMetadata = z.infer<typeof CampaignStatsMetadataSchema>;
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;
export type SocialMediaPostsResponse = z.infer<
  typeof SocialMediaPostsResponseSchema
>;

/**
 * Social stream types supported by the API
 */
export const SOCIAL_STREAMS = ['feed', 'story', 'reels'] as const;
export type SocialStream = (typeof SOCIAL_STREAMS)[number];

/**
 * Social stream tab types including the combined "all" view
 */
export const SOCIAL_STREAM_TABS = ['all', ...SOCIAL_STREAMS] as const;
export type SocialStreamTab = (typeof SOCIAL_STREAM_TABS)[number];

/**
 * Sortable columns for social media posts
 */
export type SocialMediaPostSortField =
  | 'clickthrough_count'
  | 'closeup_impression_count'
  | 'comment_count'
  | 'created_at'
  | 'engagement_count'
  | 'engagement_rate'
  | 'follower_count'
  | 'impression_count'
  | 'like_count'
  | 'posted_at'
  | 'reach_count'
  | 'reply_count'
  | 'repost_count'
  | 'share_count'
  | 'total_interactions_count'
  | 'video_average_watch_time'
  | 'video_views_organic'
  | 'video_views_paid'
  | 'favorites_organic'
  | 'favorites_paid'
  | 'saved_count'
  | 'view_count';

/**
 * Query parameters for fetching social media posts
 */
export interface SocialMediaPostsQueryParams {
  campaignId: string;
  page?: number;
  perPage?: number;
  sort?: string;
  q?: string;
  socialStream?: SocialStream;
}

/**
 * Column visibility configuration for the posts table
 */
export interface PostsTableColumnVisibility {
  post: boolean;
  creator: boolean;
  followers: boolean;
  impressions: boolean;
  comments: boolean;
  likes: boolean;
  saves: boolean;
  views: boolean;
  avgWatchTime: boolean;
  videoViewsOrganic: boolean;
  videoViewsPaid: boolean;
  favoritesOrganic: boolean;
  favoritesPaid: boolean;
  shares: boolean;
  engagementRate: boolean;
  postedAt: boolean;
}

/**
 * Default column visibility for the posts table
 */
export const DEFAULT_POSTS_TABLE_COLUMN_VISIBILITY: PostsTableColumnVisibility =
  {
    post: true,
    creator: true,
    followers: true,
    impressions: true,
    comments: true,
    likes: true,
    saves: true,
    views: true,
    avgWatchTime: true,
    videoViewsOrganic: true,
    videoViewsPaid: true,
    favoritesOrganic: true,
    favoritesPaid: true,
    shares: true,
    engagementRate: true,
    postedAt: true,
  };
